import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Eye, EyeOff, Shield, Users, Lock, CheckCircle, AlertTriangle, ExternalLink, Calendar, User} from 'lucide-react'

export default function NurseVisibilitySettings() {
  const [settings, setSettings] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [viewLogs, setViewLogs] = useState<any[]>([])
  const [showLogs, setShowLogs] = useState(false)
  const [allRecruiters, setAllRecruiters] = useState<any[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    fetchSettings()
    fetchViewLogs()
    fetchRecruiters()
  }, [])

  const fetchSettings = async () => {
    const user = lumi.auth.user
    if (!user) {
      navigate("/auth")
      return
    }

    try {
      const { list } = await lumi.entities.NurseVisibilitySettings.list({
        filter: { userId: user.userId },
        limit: 1
      })

      if (list.length > 0) {
        setSettings(list[0])
      } else {
        // Create default settings
        const defaultSettings = await lumi.entities.NurseVisibilitySettings.create({
          userId: user.userId,
          visibilityMode: "public",
          allowLimitedProfileAccess: true,
          allowContactInfo: true,
          allowDocumentDownload: true,
          allowMessaging: true,
          allowSalaryView: true,
          selectiveRecruiterWhitelist: [],
          createdAt: new Date().toISOString()
        })
        setSettings(defaultSettings)
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to load visibility settings")
    } finally {
      setLoading(false)
    }
  }

  const fetchViewLogs = async () => {
    const user = lumi.auth.user
    if (!user) return

    try {
      const { list } = await lumi.entities.ProfileViewAuditLog.list({
        filter: { nurseUserId: user.userId },
        sort: { viewedAt: -1 },
        limit: 30
      })
      setViewLogs(list)
    } catch (error) {
      console.error("Failed to load view logs:", error)
    }
  }

  const fetchRecruiters = async () => {
    try {
      const { list } = await lumi.entities.RecruiterMOU.list({
        filter: { mouStatus: "signed" },
        limit: 100
      })
      setAllRecruiters(list)
    } catch (error) {
      console.error("Failed to load recruiters:", error)
    }
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    try {
      const updated = await lumi.entities.NurseVisibilitySettings.update(settings._id, {
        ...settings,
        updatedAt: new Date().toISOString()
      })
      setSettings(updated)
      toast.success("Visibility settings saved successfully")
    } catch (error) {
      toast.error("Failed to save settings")
    } finally {
      setSaving(false)
    }
  }

  const toggleRecruiterWhitelist = (recruiterId: string) => {
    const whitelist = settings.selectiveRecruiterWhitelist || []
    if (whitelist.includes(recruiterId)) {
      setSettings({
        ...settings,
        selectiveRecruiterWhitelist: whitelist.filter((id: string) => id !== recruiterId)
      })
    } else {
      setSettings({
        ...settings,
        selectiveRecruiterWhitelist: [...whitelist, recruiterId]
      })
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard/nurse")} className="text-gray-600 hover:text-gray-900">
              ← Back
            </button>
            <h1 className="text-xl font-bold text-gray-900">Privacy & Visibility Settings</h1>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* MOU Protection Notice */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border-2 border-emerald-200">
          <div className="flex items-start gap-4">
            <Shield className="w-8 h-8 text-emerald-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">🔒 Your Profile is Protected</h2>
              <p className="text-gray-700 mb-3">
                Recruiters who haven't signed a Nursing Hive MOU (Memorandum of Understanding) can <strong>only see limited information</strong>:
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  <span>Your name</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  <span>Your country</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  <span>Specialization summary</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="w-4 h-4" />
                  <span>Education level</span>
                </div>
              </div>
              <p className="text-gray-700 mb-2">
                Your <strong>detailed information</strong> (documents, certificates, contact info, full CV) is shared <strong>only</strong> with verified, MOU-signed recruiters.
              </p>
              <a href="/resources/mou" className="text-emerald-600 hover:text-emerald-700 text-sm flex items-center gap-1">
                Learn more about our MOU process <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Visibility Mode Selection */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Who Can See Your Full Profile?</h3>
          <p className="text-sm text-gray-600 mb-6">Choose who can view your complete information (applies only to MOU-signed recruiters)</p>

          <div className="space-y-4">
            {/* Public Mode */}
            <label className="flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="visibilityMode"
                value="public"
                checked={settings?.visibilityMode === "public"}
                onChange={(e) => setSettings({ ...settings, visibilityMode: e.target.value })}
                className="mt-1 w-5 h-5 text-emerald-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-gray-900">Public (Recommended)</span>
                </div>
                <p className="text-sm text-gray-600">
                  All MOU-signed recruiters can see your complete profile. This maximizes your visibility to legitimate recruiters.
                </p>
              </div>
            </label>

            {/* Selective Mode */}
            <label className="flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="visibilityMode"
                value="selective"
                checked={settings?.visibilityMode === "selective"}
                onChange={(e) => setSettings({ ...settings, visibilityMode: e.target.value })}
                className="mt-1 w-5 h-5 text-emerald-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">Selective</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Only specific recruiters you approve can see your full profile. All others see limited information only.
                </p>
                {settings?.visibilityMode === "selective" && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Select approved recruiters:</p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {allRecruiters.length === 0 ? (
                        <p className="text-sm text-gray-500">No MOU-signed recruiters available yet.</p>
                      ) : (
                        allRecruiters.map(recruiter => (
                          <label key={recruiter._id} className="flex items-center gap-2 p-2 hover:bg-blue-100 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(settings.selectiveRecruiterWhitelist || []).includes(recruiter.userId)}
                              onChange={() => toggleRecruiterWhitelist(recruiter.userId)}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-sm text-gray-700">{recruiter.companyName || recruiter.userId}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </label>

            {/* Private Mode */}
            <label className="flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="visibilityMode"
                value="private"
                checked={settings?.visibilityMode === "private"}
                onChange={(e) => setSettings({ ...settings, visibilityMode: e.target.value })}
                className="mt-1 w-5 h-5 text-emerald-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-gray-600" />
                  <span className="font-semibold text-gray-900">Private</span>
                </div>
                <p className="text-sm text-gray-600">
                  Hide your profile from all recruiters. Your profile won't be visible to anyone. Ideal for passive candidates.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Detailed Settings */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Privacy Controls</h3>
          <p className="text-sm text-gray-600 mb-6">Fine-tune what MOU-signed recruiters can see (applies within your chosen visibility mode)</p>

          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings?.allowContactInfo || false}
                  onChange={(e) => setSettings({ ...settings, allowContactInfo: e.target.checked })}
                  className="w-4 h-4 text-emerald-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Allow viewing of contact information</p>
                  <p className="text-xs text-gray-500">Email, phone, WhatsApp number</p>
                </div>
              </div>
            </label>

            <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings?.allowDocumentDownload || false}
                  onChange={(e) => setSettings({ ...settings, allowDocumentDownload: e.target.checked })}
                  className="w-4 h-4 text-emerald-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Allow download of documents</p>
                  <p className="text-xs text-gray-500">Certificates, CV, licenses</p>
                </div>
              </div>
            </label>

            <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings?.allowMessaging || false}
                  onChange={(e) => setSettings({ ...settings, allowMessaging: e.target.checked })}
                  className="w-4 h-4 text-emerald-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Allow messaging from recruiters</p>
                  <p className="text-xs text-gray-500">Recruiters can send you direct messages</p>
                </div>
              </div>
            </label>

            <label className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings?.allowSalaryView || false}
                  onChange={(e) => setSettings({ ...settings, allowSalaryView: e.target.checked })}
                  className="w-4 h-4 text-emerald-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Allow viewing of salary expectations</p>
                  <p className="text-xs text-gray-500">Your expected salary range</p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Activity Log */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Profile View Activity (Last 30 Days)</h3>
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="text-sm text-emerald-600 hover:text-emerald-700"
            >
              {showLogs ? "Hide" : "Show"} Activity
            </button>
          </div>

          {showLogs && (
            <div className="space-y-2">
              {viewLogs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No profile views yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 text-gray-700 font-medium">Recruiter</th>
                        <th className="text-left p-3 text-gray-700 font-medium">Company</th>
                        <th className="text-left p-3 text-gray-700 font-medium">Date</th>
                        <th className="text-left p-3 text-gray-700 font-medium">Access Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewLogs.map((log, idx) => (
                        <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="p-3 text-gray-900">{log.recruiterName || "Unknown"}</td>
                          <td className="p-3 text-gray-600">{log.recruiterCompany || "N/A"}</td>
                          <td className="p-3 text-gray-600">{new Date(log.viewedAt).toLocaleDateString()}</td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              log.dataAccessLevel === "full" 
                                ? "bg-green-100 text-green-700" 
                                : "bg-gray-100 text-gray-600"
                            }`}>
                              {log.dataAccessLevel === "full" ? "Full Profile" : "Limited Profile"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
